import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import type { IntakeFile } from '@/lib/templateIntakes'
import type { SourceTemplate } from '@/lib/types'
import './template-intake.css'

/** Decimal units, as file browsers show them: 13 B, 4.2 MB. */
export function fileSize(bytes: number) {
  if (bytes < 1000) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1000
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) { value /= 1000; unit++ }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`
}

/**
 * A submitted template, read as reference material: the data files and the notes that came with
 * them. It folds to one summary line so the proposals built from it stay the focus of the page,
 * and opens in place for whoever needs to check what the expert actually sent.
 */
export function TemplateSource({ files, explanation, author, initiallyOpen, busy, onDownloadFile, onDownloadNotes, editor }: {
  files: IntakeFile[]; explanation: string; author: string; initiallyOpen: boolean; busy: boolean
  onDownloadFile: (fileId: string) => void; onDownloadNotes: () => void
  editor?: { onChange: (text: string) => void; onAttach: (files: FileList) => void; onRemove: (id: string) => Promise<void>
    onSave: () => Promise<void>; status: string; progress: string; changed: boolean }
}) {
  const [open, setOpen] = useState(initiallyOpen)
  const [editing, setEditing] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [removing, setRemoving] = useState<{ file: IntakeFile; button: HTMLButtonElement } | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const id = useId()
  const total = files.reduce((sum, file) => sum + file.size, 0)
  return <section className="template-source" aria-labelledby={`${id}-title`}>
    <header className="template-source-head">
      <div className="template-source-summary">
        <h2 id={`${id}-title`}><Icon name="paperclip" size={16} />Source data</h2>
        <span className="muted small">{files.length === 1 ? '1 file' : `${files.length} files`} · {fileSize(total)} · Submitted by {author}</span>
      </div>
      <div className="row template-source-actions">
        <button type="button" className="ghost" aria-expanded={open} aria-controls={`${id}-body`} onClick={() => setOpen(on => !on)}>
          {open ? 'Hide source data' : 'Show source data'}
        </button>
      </div>
    </header>
    {!open && explanation && <p className="template-source-preview">{explanation}</p>}
    {open && <div id={`${id}-body`} className="template-source-body">
      {editor?.progress && <p className="small muted" role="status">{editor.progress}</p>}
      {!!files.length && <ul className="intake-files" aria-label="Data files">{files.map(file => <li key={file.file_id}>
        <Icon name="file" size={18} />
        <span className="intake-file-name">{file.filename}<small>{fileSize(file.size)}{file.status === 'pending' && ' · Upload unfinished'}</small></span>
        {file.status === 'uploaded' && <button className="ghost" disabled={busy} onClick={() => onDownloadFile(file.file_id)}><Icon name="download" size={15} />Download</button>}
        {editor && <button type="button" className="ghost template-source-remove" disabled={busy} aria-label={`Remove ${file.filename}`} title="Remove file"
          onClick={event => setRemoving({ file, button: event.currentTarget })}><Icon name="trash" size={16} /></button>}
      </li>)}</ul>}
      {editor && <><button type="button" aria-label="Upload source data files" disabled={busy}
        onClick={() => fileInput.current?.click()} className={`template-source-dropzone${dragging ? ' is-dragging' : ''}${busy ? ' is-disabled' : ''}`}
        onDragOver={event => {
          if (!event.dataTransfer.types.includes('Files')) return
          event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = busy ? 'none' : 'copy'
          if (!busy) setDragging(true)
        }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false) }}
        onDrop={event => {
          event.preventDefault(); event.stopPropagation(); setDragging(false)
          if (!busy && event.dataTransfer.files.length) editor.onAttach(event.dataTransfer.files)
        }}>
        <Icon name="upload" size={18} />
        <span className="template-source-upload-label">Drop files or click to upload</span>
        <span>Up to 10 files · 5 GiB each</span>
      </button>
        <input ref={fileInput} type="file" className="visually-hidden" aria-label="Add source data files" tabIndex={-1} multiple disabled={busy}
          onChange={event => { if (event.target.files?.length) editor.onAttach(event.target.files); event.target.value = '' }} />
      </>}
      <div className="template-source-notes">
        <div className="template-source-notes-head">
          <h3>{editing ? <label htmlFor={`${id}-explanation`}>Template explanation</label> : 'Template explanation'}</h3>
          {editor && <button type="button" className="ghost" disabled={busy}
            aria-label={editing ? 'Finish editing template explanation' : 'Edit template explanation'}
            onClick={() => {
              if (editing) void editor.onSave().then(() => setEditing(false)).catch(() => {})
              else setEditing(true)
            }}><Icon name={editing ? 'check' : 'pencil'} size={14} />{editing ? 'Done' : 'Edit'}</button>}
        </div>
        {editing && editor ? <textarea id={`${id}-explanation`} className="template-source-explanation" autoFocus rows={8} value={explanation} disabled={busy}
          onChange={event => editor.onChange(event.target.value)} />
          : explanation ? <p className="prewrap">{explanation}</p> : <p className="muted">No notes were written.</p>}
        <button className="ghost" disabled={busy} onClick={onDownloadNotes}><Icon name="download" size={16} />Download explanation</button>
      </div>
      {editor && (editor.status || editor.changed) && <p className="small muted template-source-save" role="status">{editor.status || 'All changes saved'}
        {editor.changed && ' · Updated source will be included in your decision.'}</p>}
    </div>}
    {editor && removing && <RemoveSourceFile file={removing.file} busy={busy} returnFocus={removing.button}
      onCancel={() => setRemoving(null)} onConfirm={async () => { await editor.onRemove(removing.file.file_id); setRemoving(null) }} />}
  </section>
}

/** Reuse the platform's native modal, focus handling and confirmation styles. */
function RemoveSourceFile({ file, busy, returnFocus, onCancel, onConfirm }: {
  file: IntakeFile; busy: boolean; returnFocus: HTMLButtonElement; onCancel: () => void; onConfirm: () => Promise<void>
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const id = useId()
  useEffect(() => {
    const element = dialog.current!
    element.showModal()
    return () => { element.close(); if (returnFocus.isConnected) returnFocus.focus() }
  }, [returnFocus])
  return createPortal(<dialog ref={dialog} className="review-confirmation source-file-confirmation"
    aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
    onCancel={event => { event.preventDefault(); if (!busy) onCancel() }}>
    <h2 id={`${id}-title`}>Remove this file?</h2>
    <p id={`${id}-description`}>{file.filename} will no longer be included in the template’s source data.</p>
    <div className="row">
      <button type="button" autoFocus disabled={busy} onClick={onCancel}>Cancel</button>
      <button type="button" className="danger" disabled={busy} onClick={() => void onConfirm()}>{busy ? 'Removing…' : 'Remove file'}</button>
    </div>
  </dialog>, document.body)
}

/** "From template …": the way back from a workflow to the data it was proposed from. */
export function TemplateLink({ source, developer }: { source: SourceTemplate; developer: boolean }) {
  return <Link className="template-link" to={`/${developer ? 'eng' : 'sme'}/templates/${source.template_id}`}>
    <Icon name="template" size={14} />From template: {source.title || 'source data'}</Link>
}
